// Local "course database" for user-saved courses. Today this is just
// a localStorage list — the shape is identical to what an org-wide
// shared backend (Cosmos / SQL / etc.) would expose, so swapping in a
// real source later is a contained change.
//
// The static fixtures in mockCourses.ts (Georgian Bay Club) stay where
// they are — CourseSelect merges them with whatever this returns so
// every searchable course flows through one list.

import { Course, Hole, Tee } from "./types";

const KEY = "asksmart.caddiegames.courses";
const ID_PREFIX = "uc_";

export type CourseSource = "manual" | "upload";

export interface SavedCourse extends Course {
  source: CourseSource;
}

export interface NewCourseInput {
  name: string;
  city?: string;
  region?: string;
  country?: string;
  holes: Hole[];
  tees?: Tee[];
  source: CourseSource;
}

// Display string for a course's location. Prefers the structured fields
// (city, region, country) joined in that order; falls back to the legacy
// freeform `location` for courses saved before the split.
export function formatCourseLocation(c: {
  city?: string;
  region?: string;
  country?: string;
  location?: string;
}): string {
  const parts = [c.city, c.region, c.country]
    .map((s) => (s || "").trim())
    .filter(Boolean);
  if (parts.length) return parts.join(", ");
  return (c.location || "").trim();
}

export function loadCourses(): SavedCourse[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist(list: SavedCourse[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // private mode / quota — best effort
  }
}

export function addCourse(input: NewCourseInput): SavedCourse {
  const record: SavedCourse = {
    id: `${ID_PREFIX}${Math.random().toString(36).slice(2, 10)}`,
    name: input.name.trim(),
    city: input.city?.trim() || undefined,
    region: input.region?.trim() || undefined,
    country: input.country?.trim() || undefined,
    holes: input.holes,
    tees: input.tees,
    source: input.source,
  };
  const list = loadCourses();
  list.push(record);
  persist(list);
  return record;
}

// Update an existing course in place (by id), or add it if it's not yet
// saved. Used by the "View card → Edit" flow: editing a saved course
// overwrites it; editing a static/fixture course writes a saved override
// keyed by the same id (CourseSelect dedupes so the override wins).
export function upsertCourse(
  course: Course,
  source: CourseSource,
): SavedCourse {
  const record: SavedCourse = {
    id: course.id,
    name: course.name.trim(),
    city: course.city?.trim() || undefined,
    region: course.region?.trim() || undefined,
    country: course.country?.trim() || undefined,
    holes: course.holes,
    tees: course.tees,
    source,
  };
  const list = loadCourses();
  const idx = list.findIndex((c) => c.id === course.id);
  if (idx >= 0) list[idx] = record;
  else list.push(record);
  persist(list);
  return record;
}

export function deleteCourse(id: string): void {
  persist(loadCourses().filter((c) => c.id !== id));
}
